import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, ReadBefore, ReadBeforeSummary } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Avatar, Empty, Spinner, useScrollLock, useSheetDrag, EmptyState } from "../../components/ui";
import { RecapView } from "./RecapPage";
import { Cover, authorsOf } from "../../components/BookBits";
import { Book3D } from "../../components/Book3D";
import { ChevronIcon, CloseIcon, MoreIcon, PlusIcon } from "../../components/Icons";
import { NoteEditor, WantButton, factsFor, useSuggestionRefresh } from "../../components/Suggestion";
import { useClub } from "./ClubLayout";
import { PickBook } from "../../components/PickBook";
import { DatesEditor } from "../../components/DatesEditor";
import { RankMark, Tally, VoteHeader, toggleRank, useVote } from "../../components/NextBookVote";
import { StartBook } from "../../components/StartBook";
import { useClubBooks, useMe } from "../../lib/queries";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.tab;

const day = (iso: string, year = false) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", ...(year ? { year: "numeric" } : {}) });
const datesOf = (b: ClubBook) => b.finishedAt ? `${b.startedAt ? `${day(b.startedAt)} – ` : txt.finished}${day(b.finishedAt, true)}` : null;
const yearOf = (b: ClubBook) => (b.finishedAt ? new Date(b.finishedAt).getFullYear() : null);
const daysOf = (b: ClubBook) => b.startedAt && b.finishedAt ? Math.max(1, Math.round((new Date(b.finishedAt).getTime() - new Date(b.startedAt).getTime()) / 86_400_000)) : null;
/** The title page inside a shelf book: dates, the club's rating, and a date stamp. */
const titlePage = (b: ClubBook) => ({
  dates: datesOf(b), days: daysOf(b), stars: b.ratings?.average ?? null, count: b.ratings?.count ?? 0,
  stamp: b.finishedAt ? txt.read(new Date(b.finishedAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })) : null,
});

/** "Dom and Kay read it · Kay would sit it out", you first. */
export function readByLine(rb: ReadBeforeSummary, meId?: string, count = false): string | null {
  if (rb.readers.length === 0) return null;
  const name = (r: ReadBeforeSummary["readers"][number]) => (r.id === meId ? txt.you : r.name);
  const names = [...rb.readers].sort((a, b) => Number(b.id === meId) - Number(a.id === meId)).map(name);
  const list = names.length <= 3 ? names.join(names.length === 2 ? " and " : ", ").replace(/, ([^,]*)$/, " and $1") : txt.andMore(names.slice(0, 2).join(", "), names.length - 2);
  const who = count ? txt.ofHaveReadIt(rb.readers.length, rb.members) : txt.readIt(list);
  const skip = rb.readers.filter((r) => r.skip);
  const sit = skip.length === 0 ? "" : skip.length === 1 ? txt.sitItOut(skip[0]!.id === meId ? txt.youD : txt.would(skip[0]!.name)) : txt.wouldSitItOut(skip.length);
  return who + sit;
}

/** Small faces of who's read it; a dimmed face with a dash would sit it out. */
function ReadFaces({ rb }: { rb: ReadBeforeSummary }) {
  return <span className="faces">{rb.readers.map((r) => <span key={r.id} className={`face${r.skip ? " skip" : ""}`} title={`${r.name}${r.skip ? txt.wouldSitItOut2 : ""}`}><Avatar name={r.name} image={r.image} />{r.skip && <u />}</span>)}</span>;
}

/** The one follow-up after "I've read this": read it again, or sit it out. Also the way to take the mark back. */
function ReadAsk({ clubId, book, mine, onDone }: { clubId: string; book: ClubBook; mine: ReadBefore; onDone: () => void }) {
  const qc = useQueryClient();
  const set = useMutation({ mutationFn: (state: ReadBefore) => api.setReadBefore(clubId, book.id, state), onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); onDone(); } });
  return (
    <div className="ask">
      <span className="muted">{txt.readItAgain}</span>
      <button type="button" className="btn sm" disabled={set.isPending || mine === "reread"} onClick={() => set.mutate("reread")}>{txt.happyTo}</button>
      <button type="button" className="btn sm" disabled={set.isPending || mine === "skip"} onClick={() => set.mutate("skip")}>{txt.iDSitIt}</button>
      {mine !== "unread" && <button type="button" className="btn ghost sm" disabled={set.isPending} onClick={() => set.mutate("unread")}>{txt.havenTReadIt}</button>}
      <button type="button" className="linkish muted" style={{ marginLeft: "auto" }} onClick={onDone}>{txt.cancel}</button>
      {set.isError && <span className="error small" style={{ flexBasis: "100%" }}>{describeError(set.error)}</span>}
    </div>
  );
}

export const pickedLine = (p: ClubBook["picked"], meId?: string): string | null =>
  !p ? null : p.how === "vote" ? txt.pickedByVoteOf(p.points, p.points === 1 ? txt.point : txt.points, p.voters, p.members, p.tied ? txt.tieBroken : "")
    : p.how === "turn" ? txt.pick(p.user.id === meId ? txt.your : txt.s(p.user.name)) : null;

/** Books per plank. Phones: two, big enough to read. Desktop: up to six, fewer on a narrow window; the books grow to fill (capped in CSS). */
const DESK_SLOT = 170; // the narrowest a desktop book should get, gap included
const DESK_MAX_COLS = 6;
function useCols(el: HTMLElement | null, phone = 2) {
  const [cols, setCols] = useState(phone);
  useEffect(() => {
    if (!el) return;
    const calc = () => setCols(window.matchMedia("(min-width: 900px)").matches ? Math.min(DESK_MAX_COLS, Math.max(3, Math.floor((el.clientWidth + 28) / DESK_SLOT))) : window.matchMedia("(min-width: 600px)").matches ? Math.max(3, phone + 1) : phone);
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, phone]);
  return cols;
}

/** Below desktop width: shelves swipe, and the recap is a sheet. */
function useNarrow() {
  const q = "(max-width: 899px)";
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

/** One plank you swipe along, the next cover peeking at the edge. */
function ShelfRun({ children }: { children: React.ReactNode }) {
  return <div className="hshelf"><div className="plank run">{children}</div></div>;
}

/** A finished book's recap, slid up over the shelf on a phone: the same page, as a sheet you pull down to close. */
function RecapSheet({ clubId, bookId, title, onClose: unmount }: { clubId: string; bookId: string; title: string; onClose: () => void }) {
  useScrollLock();
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const onClose = useCallback(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { unmount(); return; }
    setClosing(true); window.setTimeout(unmount, 320);
  }, [unmount]);
  useSheetDrag(ref, unmount);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div className={`modal-dim book-dim${closing ? " out" : ""}`} onClick={onClose} />
      <div className={`modal book-panel recap-sheet${closing ? " out" : ""}`} role="dialog" aria-label={txt.recap(title)} ref={ref} onAnimationEnd={(e) => { if (closing && e.target === e.currentTarget) unmount(); }}>
        <div className="grab" />
        <button type="button" className="x" aria-label={txt.close} onClick={onClose}><CloseIcon /></button>
        <RecapView clubId={clubId} bookId={bookId} inSheet />
      </div>
    </>
  );
}

export function ShelfTab() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const qc = useQueryClient();
  const me = useMe();
  const books = useClubBooks(clubId);
  const navigate = useNavigate();
  const { book: openId = null } = useSearch({ from: "/authed/clubs/$clubId/shelf" });
  // The open sheet is URL state (?book=), replaced in place so Back still leaves the shelf.
  const setOpenId = (id: string | null) => void navigate({ to: "/clubs/$clubId/shelf", params: { clubId }, search: id ? { book: id } : {}, replace: true });
  // Phones and tablets: shelves swipe sideways, and a finished book's recap slides up as a sheet. Desktop: wrapped planks, and the recap is a page.
  const narrow = useNarrow();
  const openFinished = (id: string) => (narrow ? setOpenId(id) : void navigate({ to: "/clubs/$clubId/recap/$bookId", params: { clubId, bookId: id } }));
  const [backCatalogue, setBackCatalogue] = useState(false); // "Add books you've read before"
  const [finishedEl, setFinishedEl] = useState<HTMLElement | null>(null); // callback ref: the section only exists once the books load
  const cols = useCols(finishedEl);
  const [deckEl, setDeckEl] = useState<HTMLElement | null>(null);
  const deckCols = useCols(deckEl, 3);
  const refresh = useSuggestionRefresh(clubId);
  // On desktop, a ?book= link to a finished book lands on its recap page.
  const finishedOpen = openId ? books.data?.finished.find((b) => b.id === openId) : undefined;
  useEffect(() => {
    if (finishedOpen && !narrow) void navigate({ to: "/clubs/$clubId/recap/$bookId", params: { clubId, bookId: finishedOpen.id }, replace: true });
  }, [finishedOpen, narrow, clubId, navigate]);
  const isAdmin = c.myRole !== "member";
  const v = useVote(clubId);
  // A linked member's Hardcover read shelf pre-fills "I've read this" on the deck.
  const linked = me.data?.hardcover.linked && me.data.hardcover.status === "ok";
  useEffect(() => {
    if (!linked) return;
    void api.hardcoverSync().then(() => qc.invalidateQueries({ queryKey: ["books", clubId] })).catch(() => undefined);
  }, [linked, clubId, qc]);

  if (books.isPending) return <Spinner />;
  if (books.isError) return <p className="error">{describeError(books.error)}</p>;
  const { current, onDeck, finished, vote, turn, suggestions } = books.data;
  const canSuggest = isAdmin || c.settings.onDeckPolicy === "everyone";
  const voting = vote?.status === "open";
  const maxPts = vote ? Math.max(0, ...vote.entries.map((e) => e.points)) : 0;
  // Who may press Start: admins, or whoever's turn it is.
  const myTurn = !!turn && turn.user.id === me.data?.id;
  const canStart = isAdmin || myTurn;
  const open = openId ? onDeck.find((b) => b.id === openId) ?? null : null;
  // During a closed vote the result card holds the winner (or the tied books); the deck shows the rest.
  const deck = onDeck.filter((b) => !(vote?.status === "closed" && (vote.winnerId === b.id || vote.tiedIds.includes(b.id))));

  // Finished books by the year the club finished them, newest first.
  const years: { year: number | null; books: ClubBook[] }[] = [];
  for (const b of finished) { const y = yearOf(b); const g = years.find((x) => x.year === y); if (g) g.books.push(b); else years.push({ year: y, books: [b] }); }
  const planksOf = (list: ClubBook[], n: number) => { const out: ClubBook[][] = []; for (let i = 0; i < list.length; i += n) out.push(list.slice(i, i + n)); return out; };
  const pagesRead = finished.reduce((n, b) => n + (b.book.pages ?? 0), 0);
  const since = finished.map(yearOf).filter((y): y is number => y !== null).sort()[0];
  const recapOpen = narrow && finishedOpen ? finishedOpen : null;
  const deckSlot = (b: ClubBook) => (
    <div key={b.id} className="slot">
      {b.suggestion?.pinned && <span className="pinmark tag">{txt.pinned}</span>}
      <Book3D book={b.book} flyleaf={factsFor(b, c.settings, me.data?.id, true)} selected={open?.id === b.id} onClick={() => setOpenId(open?.id === b.id ? null : b.id)} />
      <div className="r">{b.suggestion?.mine && <i>✓</i>}{txt.wantToReadIt(b.suggestion?.wanted ?? 0)}</div>
    </div>
  );
  const finishedSlot = (b: ClubBook) => (
    <div key={b.id} className="slot">
      <Book3D book={b.book} page={titlePage(b)} selected={recapOpen?.id === b.id} onClick={() => openFinished(b.id)} />
      <div className="r">{b.ratings?.average != null ? <><i>★</i>{b.ratings.average.toFixed(1)}</> : <span className="muted">{txt.unrated}</span>}</div>
    </div>
  );

  return (
    <main className={`page stack shelf${open ? " with-panel" : ""}`}>
      <section className="stack-sm">
          <div className="sect"><h3>{txt.onDeck}</h3><span className="count">{c.settings.deckFill === "admins" ? txt.chosenByAdmins : voting ? txt.heldStillUntilThe : onDeck.length > 0 ? txt.theMostWanted(onDeck.length) : ""}</span></div>
          {c.settings.pickPolicy === "vote" && <VoteHeader clubId={clubId} vote={vote} isAdmin={isAdmin} canStart={canStart} current={current} meeting={c.meeting} deckCount={onDeck.length} onDeck={onDeck} />}
          {turn && (
            <div className="vote-head turn">
              <Avatar name={turn.user.name} image={turn.user.image} />
              <div className="grow"><b>{myTurn ? txt.itSYourTurn : txt.itSSTurn(turn.user.name)}</b>
                <div className="d">{myTurn ? txt.startAnyBookOn : txt.then(turn.order[(turn.order.findIndex((m) => m.id === turn.user.id) + 1) % turn.order.length]?.name ?? "")}</div></div>
            </div>
          )}
          {onDeck.length === 0 && (
            <EmptyState title={txt.nothingOnDeckYet}
              action={suggestions.running === 0 && canSuggest ? <Link to="/clubs/$clubId/suggestions" params={{ clubId }} search={{ add: true }} className="btn primary">{txt.suggestABook}</Link> : undefined}>
              {suggestions.running > 0 ? txt.tapWantToRead : txt.theBooksTheClub}
            </EmptyState>
          )}
          {voting ? (
            <div className={`deck ballot${vote.method === "approval" ? " approval" : ""}`}>
              {deck.map((b) => (
                <DeckRow key={b.id} b={b} clubId={clubId} isAdmin={isAdmin} canStart={canStart} meId={me.data?.id} current={current} onChange={refresh} onOpen={() => setOpenId(open?.id === b.id ? null : b.id)}
                  rank={<RankMark approval={vote.method === "approval"} rank={vote.myRanks.includes(b.id) ? vote.myRanks.indexOf(b.id) + 1 : null} disabled={v.ballot.isPending} onClick={() => v.ballot.mutate(toggleRank(vote.myRanks, b.id))} />}
                  tally={<Tally vote={vote} id={b.id} max={maxPts} />}
                  newMark={vote.newSinceMyVote.includes(b.id)} />
              ))}
            </div>
          ) : (
            deck.length > 0 && (narrow ? <ShelfRun>{deck.map(deckSlot)}</ShelfRun> : (
              <div className="deck-planks" ref={setDeckEl}>
                {planksOf(deck, deckCols).map((row, i) => <div key={i} className="plank deck-plank" style={{ "--cols": deckCols } as React.CSSProperties}>{row.map(deckSlot)}</div>)}
              </div>
            ))
          )}
          {(suggestions.running > 0 || onDeck.length > 0) && <Link to="/clubs/$clubId/suggestions" params={{ clubId }} className="quiet link sugg-entry">
            {suggestions.newest.length > 0 ? <span className="covers">{suggestions.newest.map((n, i) => <span key={i} className="cover sm">{n.coverUrl ? <img src={n.coverUrl} alt="" /> : null}</span>)}</span> : <PlusIcon />}
            <div style={{ minWidth: 0 }}><b>{txt.suggestions}</b><div className="d">{suggestions.running === 0 ? (onDeck.length > 0 ? txt.everythingSuggestedIsOn : txt.suggestTheFirstBook) : txt.more2(suggestions.running, suggestions.running === 1 ? txt.book : txt.books, suggestions.newThisWeek > 0 ? txt.newThisWeek(suggestions.newThisWeek) : "")}</div></div>
            <ChevronIcon className="go" />
          </Link>}
      </section>

      <section className="stack-sm finished" ref={setFinishedEl}>
        <div className="sect"><h3>{txt.finished2}</h3>{finished.length > 0 && <span className="count">{finished.length} {finished.length === 1 ? txt.book : txt.books}{pagesRead > 0 ? txt.pages(pagesRead.toLocaleString()) : ""}{since ? txt.since(since) : ""}</span>}</div>
        {finished.length === 0 && (
          <EmptyState title={txt.nothingFinishedYet}
            action={isAdmin ? <button type="button" className="btn" onClick={() => setBackCatalogue(true)}>{txt.addBooksYouVe}</button> : undefined}>{txt.booksTheClubHas}</EmptyState>
        )}
        {backCatalogue && <PickBook clubId={clubId} mode="finished" onClose={() => setBackCatalogue(false)} />}
        {years.map((g) => (
          <div key={g.year ?? "undated"} className="plank-group">
            <div className="year-head">{g.year !== null ? <span className="plate">{g.year}</span> : <span className="plate">{txt.undated}</span>}<span className="muted small">{g.books.length} {g.books.length === 1 ? txt.book : txt.books}</span></div>
            {narrow ? <ShelfRun>{g.books.map(finishedSlot)}</ShelfRun>
              : planksOf(g.books, cols).map((row, i) => <div key={i} className="plank" style={{ "--cols": cols } as React.CSSProperties}>{row.map(finishedSlot)}</div>)}
          </div>
        ))}
      </section>

      {open && <BookPanel key={open.id} b={open} clubId={clubId} isAdmin={isAdmin} canStart={canStart} meId={me.data?.id} current={current} onChange={refresh} onClose={() => setOpenId(null)} />}
      {recapOpen && <RecapSheet key={recapOpen.id} clubId={clubId} bookId={recapOpen.id} title={recapOpen.book.title} onClose={() => setOpenId(null)} />}
    </main>
  );
}

const ordinalOf = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? txt.th : ({ 1: txt.st, 2: txt.nd, 3: txt.rd } as Record<number, string>)[n % 10] ?? txt.th}`;

/** Everything about a suggestion, one tap away: a sheet on phones, a side pane on desktop. Used by the Shelf and the Suggestions page. */
export function BookPanel({ b, clubId, isAdmin, canStart, meId, current, onChange, onClose: unmount }:
  { b: ClubBook; clubId: string; isAdmin: boolean; canStart: boolean; meId?: string; current: ClubBook | null; onChange: () => Promise<unknown>; onClose: () => void }) {
  useScrollLock();
  // Closing plays the out animation first; the shelf unmounts the panel when it ends (or straight away without motion).
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const onClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true; setClosing(true);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) unmount();
    else window.setTimeout(unmount, 320); // in case animationend never fires
  }, [unmount]);
  const [finishing, setFinishing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [asking, setAsking] = useState(false);
  const [more, setMore] = useState(false);
  const [noting, setNoting] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  const booksQ = useClubBooks(clubId);
  const onDeckCount = booksQ.data?.onDeck.length;
  const { club } = useClub();
  const facts = club.data ? factsFor(b, club.data.settings, meId) : null;
  const remove = useMutation({ mutationFn: () => api.removeBook(clubId, b.id), onSuccess: async () => { await onChange(); onClose(); } });
  const pin = useMutation({ mutationFn: (p: boolean) => api.setPinned(clubId, b.id, p), onSuccess: onChange });
  const past = useMutation({ mutationFn: () => api.moveToPast(clubId, b.id), onSuccess: async () => { await onChange(); onClose(); } });
  const rb = b.readBefore;
  const sg = b.suggestion;
  const place = !sg ? null : sg.pastAt ? txt.inPast : sg.onDeck ? (sg.pinned ? txt.onDeckPinned : txt.onDeck2) : sg.rank ? txt.inTheRunning(sg.rank, sg.rank % 100 >= 11 && sg.rank % 100 <= 13 ? txt.th : ({ 1: txt.st, 2: txt.nd, 3: txt.rd } as Record<number, string>)[sg.rank % 10] ?? txt.th) : null;
  const canRemove = isAdmin || b.addedBy?.id === meId;
  const tags = [...(b.book.genres ?? []), ...(b.book.moods ?? [])];
  const panelRef = useRef<HTMLDivElement>(null);
  useSheetDrag(panelRef, unmount);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    // A click anywhere outside the pane closes it; a click on another book switches to it instead (its own handler runs).
    const onDown = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (!t || panelRef.current?.contains(t)) return;
      if (t.closest(".bk3, .deck-hit, .book-dim, .menu")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [onClose]);
  return (
    <>
      <div className={`modal-dim book-dim${closing ? " out" : ""}`} onClick={onClose} />
      <div className={`modal side book-panel${closing ? " out" : ""}`} role="dialog" aria-label={b.book.title} ref={panelRef} onAnimationEnd={(e) => { if (closing && e.target === e.currentTarget) unmount(); }}>
        <div className="grab" />
        <button type="button" className="x" aria-label={txt.close} onClick={onClose}><CloseIcon /></button>
        <div className="hd">
          {b.book.coverUrl && <div className="art" aria-hidden="true"><img src={b.book.coverUrl} alt="" /></div>}
          <Cover book={b.book} size="lg" />
          <div style={{ minWidth: 0 }}>
            <div className="t">{b.book.title}</div>
            <div className="a">{authorsOf(b.book)}{b.book.releaseYear ? ` · ${b.book.releaseYear}` : ""}{b.book.pages ? txt.pages2(b.book.pages) : ""}</div>
          </div>
        </div>
        <section className="sec">
          {/* Why, then the facts, then the club, then you, then the book's actions. */}
          <div className="sect"><h3>{sg?.onDeck ? txt.onDeck : sg?.pastAt ? txt.inPast2 : txt.suggested}</h3>{place && <span className="count">{sg?.onDeck && sg.rank ? txt.of(ordinalOf(sg.rank), onDeckCount ?? sg.rank) : place}</span>}</div>
          {noting ? <NoteEditor clubId={clubId} b={b} onDone={() => setNoting(false)} />
            : b.note ? (
              <blockquote className="qblock note pitch"><span className="qmark" aria-hidden="true">“</span><span>{b.note}</span><span className="by">— {b.addedBy?.id === meId ? txt.you2 : b.addedBy?.name}
                {b.addedBy?.id === meId && <button type="button" className="linkish small muted" style={{ marginLeft: 8 }} onClick={() => setNoting(true)}>{txt.edit}</button>}</span></blockquote>
            ) : b.addedBy?.id === meId && sg && !sg.pastAt ? <button type="button" className="linkish small" style={{ alignSelf: "flex-start" }} onClick={() => setNoting(true)}>{txt.addANoteOn}</button> : null}
          <div className="pane-facts">{[facts?.length?.replace(/^\d[\d,]* pages · /, "").replace(/^\d[\d,]* pages$/, "") || null, sg?.readIn ? txt.theClubReadThis(sg.readIn) : null, b.note ? txt.suggested2(day(b.addedAt, true)) : txt.suggestedBy(b.addedBy ? (b.addedBy.id === meId ? txt.you2 : b.addedBy.name) : txt.someone, day(b.addedAt, true))].filter(Boolean).join(" · ")}</div>
          {sg?.movesAt && <div className="small notice">{txt.movesToPastOn(day(sg.movesAt))}</div>}
          {sg && (sg.wanted > 0 || (rb?.readers.length ?? 0) > 0) && (
            <div className="pane-club">
              <span className="faces">{sg.wantedBy.slice(0, 8).map((u) => <span key={u.id} className={`face${rb?.readers.some((r) => r.id === u.id) ? " chk" : ""}`} title={u.name}><Avatar name={u.name} image={u.image} /></span>)}</span>
              <div style={{ minWidth: 0 }}>
                <b>{txt.wantToReadIt2(sg.wanted)}</b>
                {rb && rb.readers.length > 0 && <div className="small muted">{txt.readItBefore(rb.readers.length, rb.readers.length === 1 ? txt.has : txt.have, rb.readers.some((r) => r.skip) ? txt.wouldSitItOut(rb.readers.filter((r) => r.skip).length) : "")}</div>}
              </div>
            </div>
          )}
          {sg && !asking && (
            <div className="pane-mine">
              <WantButton clubId={clubId} b={b} meId={meId} />
              {rb && <button type="button" className="linkish small muted" onClick={() => setAsking(true)}>
                {rb.mine === "unread" ? txt.iVeReadThis : txt.change(rb.mine === "skip" ? txt.youDSitIt : txt.youDReRead, rb.fromHardcover ? txt.fromYourHardcoverShelf : "")}
              </button>}
            </div>
          )}
          {rb && asking && <ReadAsk clubId={clubId} book={b} mine={rb.mine} onDone={() => setAsking(false)} />}
          {(canStart || isAdmin || canRemove) && !finishing && !removing && (
            <div className="pane-acts" ref={menuRef}>
              {canStart && !sg?.pastAt && <StartBook clubId={clubId} book={b} current={current} size="" onDone={onClose} />}
              {(isAdmin || canRemove) && <button type="button" className="more" aria-label={txt.more} aria-expanded={menu} onClick={() => setMenu((m) => !m)}><MoreIcon width={18} height={18} /></button>}
              {menu && (
                <div className="menu" role="menu">
                  {isAdmin && !sg?.pastAt && <button role="menuitem" disabled={pin.isPending} onClick={() => { setMenu(false); pin.mutate(!sg?.pinned); }}>{sg?.pinned ? txt.unpinFromOnDeck : txt.pinToOnDeck}</button>}
                  {isAdmin && <button role="menuitem" onClick={() => { setMenu(false); setFinishing(true); }}>{txt.moveToFinishedShelf}</button>}
                  {canRemove && !sg?.pastAt && <button role="menuitem" disabled={past.isPending} onClick={() => { setMenu(false); past.mutate(); }}>{txt.moveToPast}</button>}
                  {canRemove && <button role="menuitem" className="danger" onClick={() => { setMenu(false); setRemoving(true); }}>{txt.remove}</button>}
                </div>
              )}
            </div>
          )}
          {removing && (
            <div className="row small" style={{ gap: 6 }}>
              <span className="muted">{txt.takeItOffThe}</span>
              <button className="btn danger sm" disabled={remove.isPending} onClick={() => remove.mutate()}>{txt.remove}</button>
              <button className="btn ghost sm" onClick={() => setRemoving(false)}>{txt.keep}</button>
            </div>
          )}
          {finishing && <DatesEditor clubId={clubId} book={b} finish onDone={() => setFinishing(false)} />}
          {(remove.error ?? pin.error ?? past.error) && <p className="error small">{describeError(remove.error ?? pin.error ?? past.error)}</p>}
        </section>
        {/* About the book comes after the club's own say; it opens by itself when nobody wrote a note, since then the blurb is the pitch. */}
        {(b.book.description || tags.length > 0 || (b.book.contentWarnings?.length ?? 0) > 0) && (
          <details className="fold about-fold" open={!b.note}>
            <summary><span className="sect"><h3>{txt.aboutTheBook}</h3></span><ChevronIcon className="chev" width={16} height={16} aria-hidden="true" /></summary>
            <div className="sec about" style={{ paddingTop: 10 }}>
              {b.book.description && (
                <p className={`desc${more ? " more" : ""}`}>{b.book.description.replace(/\*\*?|__?/g, "")}{!more && <button type="button" className="linkish more-btn" onClick={() => setMore(true)}>more</button>}</p>
              )}
              {tags.length > 0 && <div className="pills">{tags.map((t) => <span key={t} className="pill">{t}</span>)}</div>}
              {(b.book.contentWarnings?.length ?? 0) > 0 && (
                <details className="fold cw"><summary>{txt.contentWarnings(b.book.contentWarnings!.length)}</summary><div className="pills">{b.book.contentWarnings!.map((t) => <span key={t} className="pill">{t}</span>)}</div></details>
              )}
            </div>
          </details>
        )}
        <div className="acts">
          {b.book.url && <a href={b.book.url} target="_blank" rel="noopener" className="hc-link">{txt.openOnHardcover}</a>}
        </div>
      </div>
    </>
  );
}

/** One line of the pile: small cover, the book, who suggested it, one Start button and a ⋯ for the rest. */
function DeckRow({ b, clubId, isAdmin, canStart, meId, current, onChange, onOpen, rank, tally, newMark }:
  { b: ClubBook; clubId: string; isAdmin: boolean; canStart: boolean; meId?: string; current: ClubBook | null; onChange: () => Promise<unknown>; onOpen: () => void;
    rank?: React.ReactNode; tally?: React.ReactNode; newMark?: boolean }) {
  const remove = useMutation({ mutationFn: () => api.removeBook(clubId, b.id), onSuccess: onChange });
  const [menu, setMenu] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [asking, setAsking] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const canRemove = isAdmin || b.addedBy?.id === meId;
  const rb = b.readBefore;
  useEffect(() => {
    if (!menu) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);
  const suggested = b.addedBy ? (b.addedBy.id === meId ? txt.suggestedByYou : txt.suggestedBy2(b.addedBy.name)) : null;
  return (
    <div className={`deck-row${rank ? " ranked" : ""}`} ref={ref}>
      {rank}
      <span role="button" tabIndex={0} className="deck-hit" onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }} aria-label={txt.about(b.book.title)}><Cover book={b.book} size="sm" /></span>
      <div style={{ minWidth: 0 }}>
        <div className="t"><span role="button" tabIndex={0} className="deck-hit" onClick={onOpen} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}>{b.book.title}</span></div>
        <div className="a">{authorsOf(b.book)}</div>
        {!tally && <div className="d">{[b.book.releaseYear, b.book.pages ? txt.pages3(b.book.pages) : null, suggested].filter(Boolean).join(" · ")}</div>}
        {tally && (suggested || newMark) && <div className="d">{suggested}{newMark && <>{suggested ? " · " : ""}<span className="newmark">{txt.addedSinceYouVoted}</span></>}</div>}
        {tally}
        {rb && rb.readers.length > 0 && !asking && <div className="readby"><ReadFaces rb={rb} /><span>{readByLine(rb, meId)}</span></div>}
        {rb && asking && <ReadAsk clubId={clubId} book={b} mine={rb.mine} onDone={() => setAsking(false)} />}
        {removing && (
          <div className="row small" style={{ gap: 6, marginTop: 6 }}>
            <span className="muted">{txt.takeItOffThe}</span>
            <button className="btn danger sm" disabled={remove.isPending} onClick={() => remove.mutate()}>{txt.remove}</button>
            <button className="btn ghost sm" onClick={() => setRemoving(false)}>{txt.keep}</button>
          </div>
        )}
        {finishing && <div style={{ marginTop: 8 }}><DatesEditor clubId={clubId} book={b} finish onDone={() => setFinishing(false)} /></div>}
        {handoff && <div style={{ marginTop: 8 }}><StartBook clubId={clubId} book={b} current={current} ask onDone={() => setHandoff(false)} onCancel={() => setHandoff(false)} /></div>}
        {remove.isError && <p className="error small">{describeError(remove.error)}</p>}
      </div>
      <div className="act">
        <button type="button" className="more" aria-label={txt.more} aria-expanded={menu} onClick={() => setMenu((m) => !m)}><MoreIcon width={18} height={18} /></button>
        {menu && (
          <div className="menu" role="menu">
            {canStart && <StartBook clubId={clubId} book={b} current={current} menuItem onOpen={() => { setMenu(false); setHandoff(true); }} />}
            {isAdmin && <button role="menuitem" onClick={() => { setMenu(false); setFinishing(true); }}>{txt.moveToFinishedShelf}</button>}
            {rb && <button role="menuitem" onClick={() => { setMenu(false); setAsking(true); }}>{rb.mine === "unread" ? txt.iVeReadThis : txt.readItBeforeChange}</button>}
            {canRemove && <button role="menuitem" className="danger" onClick={() => { setMenu(false); setRemoving(true); }}>{txt.remove}</button>}
          </div>
        )}
      </div>
    </div>
  );
}
